import React from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FemaleCalculatorSection } from "@/components/FemaleCalculatorSection";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSEO } from "@/hooks/useSEO";
import { getRouteMeta, canonicalUrl } from "@/seo/routeMeta";
import { datingStandardsFaq } from "@/data/landingFaqs";

const PATH = "/dating-standards-calculator";

const DatingStandardsCalculator = () => {
  const meta = getRouteMeta(PATH);

  useSEO({
    title: meta?.title ?? "Dating Standards Calculator",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: canonicalUrl(PATH),
    structuredData: meta?.structuredData,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      {/* Hero Section */}
      <header className="border-b bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Dating Standards Calculator
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              How realistic are your dating standards? Select the criteria that matter
              to you and see the statistical probability that a partner meets all of
              them — a probability of finding a partner test built entirely on US
              Census Bureau, CDC, and Pew Research data.
            </p>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-12">
        {/* Choose your calculator */}
        <section className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-primary/50">
              <CardHeader>
                <CardTitle>Standards for a female partner</CardTitle>
                <CardDescription>
                  Use the calculator below — filter US women by age, height, body
                  type, income, education, and more.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <a href="#calculator" className="text-primary hover:underline text-sm font-medium">
                  Jump to the calculator ↓
                </a>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Standards for a male partner</CardTitle>
                <CardDescription>
                  Testing standards for men? Height, income, and education filters
                  backed by male demographic data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/male-partner-calculator" className="text-primary hover:underline text-sm font-medium">
                  Open the Male Partner Calculator →
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>

        <div id="calculator" className="space-y-12">
          <FemaleCalculatorSection sharePath={PATH} />
        </div>

        {/* Standards vs probability */}
        <section className="max-w-4xl mx-auto space-y-6">
          <h2 className="text-2xl font-bold">How standards translate into probability</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Every standard you hold is a filter on the population, and filters
              multiply. If one criterion matches 40% of people and another matches
              25%, only 10% of the population passes both. Stack five or six standards
              — even individually reasonable ones — and the share of people who clear
              every bar is often a fraction of one percent. That is not pessimism; it
              is arithmetic.
            </p>
            <p>
              The useful insight is not the final number but the breakdown: which
              single standard removes the most people from your pool. Some criteria
              cost almost nothing, while others eliminate the vast majority of
              potential partners on their own. Seeing that trade-off clearly helps you
              decide which standards are worth holding firm and which are worth
              relaxing. The full multiplicative model is documented on our{" "}
              <Link to="/methodology" className="text-primary hover:underline">
                methodology page
              </Link>
              , and every input percentage is sourced on the{" "}
              <Link to="/data-sources" className="text-primary hover:underline">
                data sources page
              </Link>
              .
            </p>
            <p>
              If your standards center on describing a specific type of woman, the{" "}
              <Link to="/ideal-woman-calculator" className="text-primary hover:underline">
                ideal woman calculator
              </Link>{" "}
              frames the same engine around that question, and the{" "}
              <Link to="/stats" className="text-primary hover:underline">
                US dating statistics hub
              </Link>{" "}
              has the underlying research on income, height, education, and more.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-4xl mx-auto space-y-6">
          <h2 className="text-2xl font-bold">Frequently asked questions</h2>
          <div className="space-y-6">
            {datingStandardsFaq.map((item) => (
              <div key={item.question} className="border rounded-lg p-6">
                <h3 className="font-semibold mb-2">{item.question}</h3>
                <p className="text-sm text-muted-foreground">{item.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="max-w-4xl mx-auto">
          <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-r-lg">
            <h3 className="font-semibold mb-4">Statistical Disclaimer</h3>
            <div className="text-sm space-y-2">
              <p>
                This calculator provides statistical probabilities based on
                demographic data and should not be interpreted as definitive
                predictions about individual relationships or dating outcomes.
              </p>
              <p>
                Results reflect population-level statistics and do not account for
                geography, social circles, or individual compatibility beyond the
                measured criteria. Filters are treated as statistically independent,
                which slightly overstates rarity when preferences are correlated.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DatingStandardsCalculator;
